import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Calendar, Heart, Wallet, Search, Mail, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  event: { label: "Evento", Icon: Calendar, cls: "bg-blue-50 text-blue-700" },
  donation: { label: "Donativo", Icon: Heart, cls: "bg-amber-50 text-amber-800" },
  quota: { label: "Quota", Icon: Wallet, cls: "bg-emerald-50 text-emerald-800" },
};

export default function AdminDeclarations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/declarations`, { withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error("Erro a carregar."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const lower = q.trim().toLowerCase();
    return data.items.filter((it) => {
      if (kindFilter !== "all" && it.kind !== kindFilter) return false;
      if (!lower) return true;
      const blob = `${it.name || ""} ${it.email || ""} ${it.company || ""} ${it.billing_nif || ""} ${it.label || ""}`.toLowerCase();
      return blob.includes(lower);
    });
  }, [data, kindFilter, q]);

  const resend = async (sessionId, email) => {
    if (!sessionId) return toast.error("Sem sessão Stripe associada.");
    if (!window.confirm(`Re-enviar PDF para ${email}?`)) return;
    try {
      await axios.post(`${API}/admin/payments/${sessionId}/resend`, {}, { withCredentials: true });
      toast.success("Re-enviado.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro."); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Apagar definitivamente a Declaração Nº ${String(it.receipt_number || 0).padStart(4, "0")} (${it.name})? Esta ação é IRREVERSÍVEL.`)) return;
    try {
      await axios.delete(`${API}/admin/declarations/${it.kind}/${it.id}`, { withCredentials: true });
      toast.success("Apagado.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro."); }
  };

  const resetNumbering = async () => {
    const phrase = "REPOR";
    const input = window.prompt(
      "⚠️ ATENÇÃO — Operação IRREVERSÍVEL\n\n" +
      "Isto vai:\n" +
      "  • Apagar TODOS os recibos pagos (donativos, eventos, quotas)\n" +
      "  • Repor o contador a 0 → próximo recibo será o Nº 0001\n\n" +
      "Sócios, eventos e contactos mantêm-se intactos.\n\n" +
      `Para confirmar, escreva exatamente: ${phrase}`,
    );
    if (input !== phrase) {
      if (input !== null) toast.error("Confirmação incorreta. Operação cancelada.");
      return;
    }
    try {
      const r = await axios.post(
        `${API}/admin/receipts/reset?confirm=RESET`, {},
        { withCredentials: true },
      );
      const d = r.data?.deleted || {};
      toast.success(
        `Contador reposto. Apagados: ${d.donations || 0} donativos, ` +
        `${d.rsvps || 0} eventos, ${d.quota_payments || 0} quotas.`,
        { duration: 8000 },
      );
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro."); }
  };

  return (
    <div className="px-6 py-10 lg:px-12 lg:py-14" data-testid="admin-declarations-page">
      <div className="overline">Declarações</div>
      <h1 className="display-heading text-4xl sm:text-5xl mt-3">Todas as Declarações de Donativo.</h1>
      <span className="accent-rule mt-6 block" />
      <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
        Vista unificada de todas as declarações emitidas — inscrições em eventos, donativos livres da página Apoiar e quotas de sócios. Pesquise por nome, email, NIF ou empresa.
      </p>

      {data && (
        <>
          <div className="mt-8 grid sm:grid-cols-4 gap-3" data-testid="declarations-stats">
            <Stat label="Total emitidas" value={data.count} sub={`${data.total_eur.toFixed(2)} €`} />
            <Stat label="Eventos" value={data.by_kind.event} sub={`${(data.subtotals_eur?.event || 0).toFixed(2)} €`} tone="blue" />
            <Stat label="Donativos" value={data.by_kind.donation} sub={`${(data.subtotals_eur?.donation || 0).toFixed(2)} €`} tone="amber" />
            <Stat label="Quotas" value={data.by_kind.quota} sub={`${(data.subtotals_eur?.quota || 0).toFixed(2)} €`} tone="emerald" />
          </div>

          {data.by_event && Object.keys(data.by_event).length > 0 && (
            <div className="mt-4 bg-white border border-[hsl(var(--border))] p-4" data-testid="declarations-by-event">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-3">Por evento</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                {Object.entries(data.by_event).map(([slug, total]) => (
                  <div key={slug} className="flex justify-between items-center px-3 py-2 bg-[hsl(var(--secondary))]/60 border border-[hsl(var(--border))]/60">
                    <span className="text-xs truncate mr-2">{slug}</span>
                    <span className="text-xs font-medium font-mono">{total.toFixed(2)} €</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Pesquisar nome/email/NIF/empresa…"
            className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border border-[hsl(var(--border))] focus:outline-none focus:border-[hsl(var(--accent))]"
            data-testid="declarations-search"
          />
        </div>
        <div className="flex gap-1">
          {["all", "event", "donation", "quota"].map((k) => (
            <button key={k} onClick={() => setKindFilter(k)}
              className={`px-3 py-2 text-[11px] uppercase tracking-[0.16em] border ${kindFilter === k ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-white border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}
              data-testid={`declarations-filter-${k}`}>
              {k === "all" ? "Todas" : KIND_META[k].label}
            </button>
          ))}
        </div>
        <button onClick={resetNumbering}
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.16em] border border-red-300 text-red-700 hover:bg-red-50"
          title="Apaga recibos pagos e repõe contador a 0"
          data-testid="declarations-reset-numbering">
          Repor numeração
        </button>
      </div>

      <div className="mt-6 bg-white border border-[hsl(var(--border))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
              {["Nº", "Data", "Tipo", "Pessoa", "Email", "Empresa", "NIF", "Valor", "Origem", "Ações"].map((h) => (
                <th key={h} className="text-left text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] font-medium px-3 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">A carregar…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="declarations-empty">Sem declarações.</td></tr>
            ) : filtered.map((it) => {
              const meta = KIND_META[it.kind];
              const KIcon = meta.Icon;
              return (
                <tr key={`${it.kind}-${it.id}`} className="border-b border-[hsl(var(--border))] last:border-0 hover:bg-[hsl(var(--secondary))]/40" data-testid={`declaration-${it.kind}-${it.id}`}>
                  <td className="px-3 py-3 align-top font-mono text-xs">{String(it.receipt_number || 0).padStart(4, "0")}</td>
                  <td className="px-3 py-3 align-top text-[hsl(var(--muted-foreground))] text-xs">{it.paid_at ? new Date(it.paid_at).toLocaleDateString("pt-PT") : "—"}</td>
                  <td className="px-3 py-3 align-top">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm ${meta.cls}`}>
                      <KIcon className="h-3 w-3" /> {meta.label}
                    </span>
                  </td>
                  <td className="px-3 py-3 align-top">{it.name || "—"}</td>
                  <td className="px-3 py-3 align-top text-xs">{it.email || "—"}</td>
                  <td className="px-3 py-3 align-top text-xs">{it.company || "—"}</td>
                  <td className="px-3 py-3 align-top font-mono text-xs">{it.billing_nif || it.billing_company_nif || "—"}</td>
                  <td className="px-3 py-3 align-top font-medium">{Number(it.amount_eur || 0).toFixed(2)} €</td>
                  <td className="px-3 py-3 align-top text-xs text-[hsl(var(--muted-foreground))]">{it.label}</td>
                  <td className="px-3 py-3 align-top">
                    <div className="inline-flex items-center gap-3">
                      <a href={`${API}/admin/declarations/${it.kind}/${it.id}/pdf`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]"
                        data-testid={`declaration-open-${it.id}`}
                        title="Abrir a Declaração em PDF">
                        <FileText className="h-3.5 w-3.5" /> Abrir
                      </a>
                      {it.session_id && (
                        <button onClick={() => resend(it.session_id, it.email)}
                          className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                          data-testid={`declaration-resend-${it.id}`}>
                          <Mail className="h-3.5 w-3.5" /> Re-enviar
                        </button>
                      )}
                      <button onClick={() => remove(it)}
                        className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
                        data-testid={`declaration-delete-${it.id}`}
                        title="Apagar definitivamente">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TONE_CLS = {
  default: "bg-white border border-[hsl(var(--border))]",
  blue: "bg-blue-50 border border-blue-200",
  amber: "bg-amber-50 border border-amber-200",
  emerald: "bg-emerald-50 border border-emerald-200",
};

const Stat = ({ label, value, sub, tone = "default" }) => (
  <div className={`p-4 ${TONE_CLS[tone]}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{label}</div>
    <div className="display-heading text-3xl mt-2">{value}</div>
    {sub && <div className="mt-1 text-xs font-mono text-[hsl(var(--muted-foreground))]">{sub}</div>}
  </div>
);
